export type IsoDate = string
export type DateWindow = { since: IsoDate; through: IsoDate }

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export const BASELINE_DAYS = 28
const MILLISECONDS_PER_DAY = 86_400_000

export const shiftIsoDate = (date: IsoDate, days: number): IsoDate => {
    const shifted = new Date(`${date}T00:00:00Z`)
    shifted.setUTCDate(shifted.getUTCDate() + days)
    return shifted.toISOString().slice(0, 10)
}

/**
 * A real calendar day written as YYYY-MM-DD. The shape alone is not enough: `Date` rolls an impossible day such as
 * February 30 forward into March, so the date must survive a round trip through `Date` unchanged.
 */
export const isValidIsoDate = (value: string): boolean =>
    ISO_DATE_PATTERN.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && shiftIsoDate(value, 0) === value

/** 0 is Sunday. A date is a calendar day, so its weekday never depends on the viewer's timezone. */
export const weekdayIndexOf = (date: IsoDate): number => new Date(`${date}T00:00:00Z`).getUTCDay()

export const lengthInDays = (range: DateWindow): number =>
    Math.round(
        (Date.parse(`${range.through}T00:00:00Z`) - Date.parse(`${range.since}T00:00:00Z`)) / MILLISECONDS_PER_DAY,
    ) + 1

/** An unrecognised timezone name degrades to UTC rather than failing the whole page. */
const dateTimeFormatterFor = (timezone: string): Intl.DateTimeFormat => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' }
    try {
        return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: timezone })
    } catch (_) {
        return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: 'UTC' })
    }
}

const localDateIn = (timezone: string, now: Date): IsoDate => {
    const { year, month, day } = Object.fromEntries(
        dateTimeFormatterFor(timezone)
            .formatToParts(now)
            .map(({ type, value }) => [type, value]),
    )
    return `${year}-${month}-${day}`
}

/** Yesterday in the westernmost timezone: the newest date every listed domain has finished. */
export const latestCompleteDateAcross = (timezones: string[], now: Date = new Date()): IsoDate => {
    const zones = timezones.length ? timezones : ['UTC']
    const earliestLocalToday = zones.map((timezone) => localDateIn(timezone || 'UTC', now)).sort()[0]
    return shiftIsoDate(earliestLocalToday, -1)
}

const startOfMonth = (date: IsoDate): IsoDate => `${date.slice(0, 7)}-01`

const endOfMonth = (monthStart: IsoDate): IsoDate => {
    const nextMonthStart = new Date(`${monthStart}T00:00:00Z`)
    nextMonthStart.setUTCMonth(nextMonthStart.getUTCMonth() + 1)
    return shiftIsoDate(nextMonthStart.toISOString().slice(0, 10), -1)
}

const isWholeCalendarMonth = (range: DateWindow): boolean =>
    range.since === startOfMonth(range.since) && range.through === endOfMonth(range.since)

/** The whole calendar month before the one the date falls in. */
export const calendarMonthBefore = (date: IsoDate): DateWindow => {
    const previousMonthStart = startOfMonth(shiftIsoDate(startOfMonth(date), -1))
    return { since: previousMonthStart, through: endOfMonth(previousMonthStart) }
}

/**
 * What a range is compared with: the same number of days immediately before it, except that Last Month is
 * compared with the calendar month before it, whatever the two months' lengths.
 */
export const comparisonWindowFor = (range: DateWindow, rangeKey?: string): DateWindow => {
    if (rangeKey === 'last_month') return calendarMonthBefore(range.since)
    const length = lengthInDays(range)
    return { since: shiftIsoDate(range.since, -length), through: shiftIsoDate(range.since, -1) }
}

/** The 28 days ending the day before the range starts. */
export const baselineWindowFor = (range: DateWindow): DateWindow => ({
    since: shiftIsoDate(range.since, -BASELINE_DAYS),
    through: shiftIsoDate(range.since, -1),
})

/**
 * Everything needed to compute the range, its comparison window and its baseline: the longest of the lookbacks.
 * A whole calendar month may be compared with the longer month before it, so that month is covered too.
 */
export const fetchWindowFor = (range: DateWindow): DateWindow => {
    const sinceByLookback = shiftIsoDate(range.since, -Math.max(BASELINE_DAYS, lengthInDays(range)))
    const since = isWholeCalendarMonth(range)
        ? [sinceByLookback, calendarMonthBefore(range.since).since].sort()[0]
        : sinceByLookback
    return { since, through: range.through }
}

/** The longest window sailfish's domain subscriber history route answers; a longer one is rejected with a 400. */
export const SUBSCRIBER_HISTORY_MAXIMUM_WINDOW_DAYS = 400

/**
 * The subscriber levels the figures read: the level on the range's last day, the level the day before the range
 * (the comparison window's last day, for Last Month too), the day-over-day changes across the baseline's 28 days,
 * and the sparkline, which never reaches back past the baseline. Nothing earlier is read, so the window is the
 * baseline's start through the range's end, which stays within the route's maximum for the longest custom range.
 */
export const subscriberHistoryWindowFor = (range: DateWindow): DateWindow => ({
    since: baselineWindowFor(range).since,
    through: range.through,
})

export const datesIn = (window: DateWindow): IsoDate[] => {
    const dates: IsoDate[] = []
    for (let date = window.since; date <= window.through; date = shiftIsoDate(date, 1)) dates.push(date)
    return dates
}
